// src/components/VehicleLog/VehicleTable.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { enqueueSnackbar } from 'notistack';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Tooltip,
  ImageList,
  ImageListItem,
  Button,
  IconButton,
  Chip,
} from "@mui/material";
import {
  PhotoCamera as PhotoCameraIcon,
  Close as CloseIcon,
  ExitToApp as ExitToAppIcon,
  ImageNotSupported as ImageNotSupportedIcon,
  QrCode,
  ArrowDownward,
  ArrowUpward,
  Visibility,
  AttachMoney,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import PaymentDialog from "./Dialogs/PaymentDialog";

// Importar estilos separados
import {
  StyledPaper,
  StyledTableHeaderCell,
  StyledTableRow,
  StyledTableCell,
  StyledStatusChip,
  StyledActionButton,
  StyledImageButton,
  StyledSortLabel,
  StyledPendingText,
  formatDateTime,
  StyledText,
} from "./TableStyles";
import VehicleExitDialog from "./Dialogs/VehicleExitDialog";
import vehicleService from "../../services/vehicleService";
import { getApiBaseUrl } from "../../utils/axios";

const API_BASE = getApiBaseUrl();

// Returns a renderable src: a full URL for filenames, or the value itself for base64 data URIs.
const buildImageSrc = (value, tipo) => {
  if (!value) return null;
  if (value.startsWith('data:') || value.length > 500) return value;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  if (value.startsWith('/api/')) return `${API_BASE}${value}`;
  return `${API_BASE}/api/images/${tipo}/${value}`;
};

// Etiquetas/colores para el tipo de cobro en el detalle de pago — ver
// cobros.tipo en el backend (gratis/normal/excedido/lost_ticket/plan).
const TIPO_COBRO_LABEL = {
  gratis: "Gratis",
  excedido: "Excedente",
  lost_ticket: "Ticket perdido",
  plan: "Cubierto por plan",
};
const TIPO_COBRO_COLOR = {
  gratis: { bg: "#dcfce7", fg: "#166534" },
  excedido: { bg: "#fef3c7", fg: "#92400e" },
  lost_ticket: { bg: "#fee2e2", fg: "#991b1b" },
  plan: { bg: "#ede9fe", fg: "#6d28d9" },
};

const VehicleTable = ({ vehicles, loading, onPrintQR, onReimprimirRecibo, loadVehicles, user, orderBy = "fecha_entrada", orderDir = "desc", onSort }) => {
  const { clienteInfo } = useAuth();
  // Nombre del negocio para el recibo reimpreso: parqueadero, y su sucursal
  // si se está operando dentro de una (evita el branding "PARKEY" quemado).
  const nombreNegocio = clienteInfo?.sucursal?.nombre
    ? `${clienteInfo?.nombre || 'Parqueadero'} - ${clienteInfo.sucursal.nombre}`
    : (clienteInfo?.nombre || process.env.REACT_APP_NOMBRE_CONJUNTO || 'Parqueadero');
  const [imageDialog, setImageDialog] = useState({
    open: false,
    images: [],
    vehicleInfo: null,
  });
  const [paymentDialog, setPaymentDialog] = useState({
    open: false,
    vehicle: null,
    qrDataUrl: null,
  });
  const [exitDialog, setExitDialog] = useState({
    open: false,
    vehicle: null,
  });
  const [observationsDialog, setObservationsDialog] = useState({
    open: false,
    vehicle: null,
  });
  const [detallePagoDialog, setDetallePagoDialog] = useState({
    open: false,
    loading: false,
    data: null,
    error: null,
  });
  const [zoomImage, setZoomImage] = useState({
    open: false,
    url: '',
    name: '',
    type: '',
    timestamp: ''
  });
  // El orden real lo decide el servidor (useVehicleLog::ordenarPor) — pide
  // de nuevo la página ya ordenada por esa columna, en vez de reordenar
  // solo lo que ya estaba cargado (eso dejaba fuera a los vehículos que
  // realmente salieron más recientemente, si no estaban en la página
  // actual, traída por fecha de entrada).
  const handleSort = (property) => {
    if (onSort) onSort(property);
  };

  const handleOpenPaymentDialog = (vehicle) => {
    setPaymentDialog({
      open: true,
      vehicle: vehicle,
      qrDataUrl: null,
    });
  };
  
  const handleClosePaymentDialog = () => {
    setPaymentDialog({
      open: false,
      vehicle: null,
      qrDataUrl: null,
    });
  };
  // Minutos y horas con la misma precisión que usa el recibo, pero para
  // mostrar en pantalla (no imprimir) — ver datos-cobro en el backend.
  const formatearTiempo = (horas) => {
    if (horas === null || horas === undefined) return '-';
    const minutosTotales = Math.round(Number(horas) * 60);
    if (Number.isNaN(minutosTotales)) return '-';
    const h = Math.floor(minutosTotales / 60);
    const m = minutosTotales % 60;
    const partes = [];
    if (h > 0) partes.push(`${h} hora${h !== 1 ? 's' : ''}`);
    if (m > 0 || h === 0) partes.push(`${m} minuto${m !== 1 ? 's' : ''}`);
    return `${partes.join(' y ')} (${minutosTotales} min)`;
  };

  const handleVerDetallePago = async (vehicle) => {
    setDetallePagoDialog({ open: true, loading: true, data: null, error: null });
    try {
      const result = await vehicleService.obtenerDatosCobro(vehicle.id);
      setDetallePagoDialog({ open: true, loading: false, data: result.data, error: null });
    } catch (error) {
      setDetallePagoDialog({ open: true, loading: false, data: null, error: error.message });
    }
  };

  const handleReprintReceipt = async (vehicle) => {
  try {
    enqueueSnackbar('Obteniendo datos del recibo...', {
      variant: 'info', autoHideDuration: 2000,
      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    });

    const result = await vehicleService.obtenerDatosCobro(vehicle.id);
    const d = result.data;
    const secuencial = d.cobro_id ? String(d.cobro_id).padStart(10, '0') : '----------';

    // Configuración para el cálculo del tiempo de gracia
    // (usa el mismo valor que muestra PaymentDialog, aquí fijo 15 min como fallback)
    const minutosGraciaConfig = 15;

    const ahora = new Date();
    const fechaPago = d.fecha_pago ? new Date(d.fecha_pago) : ahora;
    const fechaEntrada = d.fecha_entrada ? new Date(d.fecha_entrada) : ahora;

    const fmt = (date) => date.toLocaleDateString('es-ES');
    const fmtTime = (date) => date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const fmtTimeShort = (date) => date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

    const horaMaximaSalida = new Date(fechaPago.getTime() + minutosGraciaConfig * 60000);

    const comp = d.comprobante;
    const clienteNombre = comp ? comp.nombre.toUpperCase() : 'CONSUMIDOR FINAL';
    const clienteId = comp ? comp.identificacion : '9999999999';
    const metodo = comp ? comp.metodo : 'efectivo';

    // Detalle de tiempo
    const minutosTotales = Math.round(d.horas_permanencia * 60);
    const hh = Math.floor(minutosTotales / 60);
    const mm = minutosTotales % 60;
    const detalleTiempo = [hh > 0 ? `${hh} hora${hh !== 1 ? 's' : ''}` : '', mm > 0 ? `${mm} minuto${mm !== 1 ? 's' : ''}` : ''].filter(Boolean).join(' y ') || '0 minutos';

    const facturas = d.facturas || [];
    const horasDescontadas = d.horas_descontadas || 0;
    const valorTicketPerdido = d.ticket_perdido ? d.valor_ticket_perdido : 0;

    const html = `<!DOCTYPE html>
        <html>
        <head>
        <meta charset="UTF-8">
        <title>Recibo Parqueo</title>
        <style>
        *{margin:0;padding:0;box-sizing:border-box}
        html,body{width:58mm;margin:0;padding:0}
        body{font-family:Arial,Helvetica,sans-serif;font-size:10px;line-height:1.2;padding:3px;color:#000;background:#fff}
        .container{width:100%;margin:0 auto}
        .header{text-align:center;border-bottom:2px solid #000;padding-bottom:3px;margin-bottom:4px}
        .logo{font-size:20px;font-weight:900;letter-spacing:2px}
        .company{font-size:11px;font-weight:bold}
        .title{font-size:9px;font-weight:bold;margin-top:1px}
        .vehicle{border:2px solid #000;text-align:center;padding:5px 3px;margin:0 auto 4px;border-radius:2px}
        .vehicle-label{font-size:8px;font-weight:bold;margin-bottom:1px}
        .plate{font-size:26px;font-weight:900;font-family:"Courier New",monospace;letter-spacing:1px}
        .grace-period{background:#f5f5f5;border:2px solid #000;padding:4px 3px;margin:0 auto 4px;text-align:center}
        .grace-label{font-size:8px;font-weight:bold;margin-bottom:2px}
        .grace-time{font-size:15px;font-weight:900;letter-spacing:1px}
        .grace-subtext{font-size:7px;margin-top:2px;font-weight:bold}
        .section{margin-bottom:4px;padding-bottom:3px;border-bottom:1px dashed #000}
        .section-title{font-weight:900;font-size:10px;border-bottom:1px solid #000;margin-bottom:3px;padding-bottom:1px;text-align:center}
        .row{display:flex;justify-content:space-between;margin-bottom:2px;font-size:10px}
        .label{font-weight:bold}
        .secuencial{text-align:center;font-size:11px;font-weight:900;font-family:"Courier New",monospace;border-bottom:1px dashed #000;padding-bottom:3px;margin-bottom:4px;letter-spacing:1px}
        .value{text-align:right}
        .breakdown{border:1px solid #000;padding:3px;font-size:9px}
        .break-row{display:flex;justify-content:space-between;margin-bottom:1px}
        .total{border:2px solid #000;text-align:center;padding:8px 4px;margin:6px auto;max-width:58mm}
        .total-amount{font-size:32px;font-weight:900;letter-spacing:1px}
        .total-label{font-size:9px;font-weight:bold}
        .client{border:1px solid #000;padding:3px;margin-bottom:4px;font-size:9px}
        .client-title{font-size:9px;font-weight:900;margin-bottom:2px;border-bottom:1px solid #000;text-align:center;padding-bottom:1px}
        .reimpresion{text-align:center;font-size:8px;font-weight:bold;border:1px dashed #000;padding:2px;margin-bottom:4px}
        .footer{text-align:center;border-top:2px solid #000;padding-top:3px;font-size:8px}
        .disclaimer{font-size:6px;margin:2px 0;font-weight:normal}
        @media print{@page{size:58mm auto;margin:0}}
        </style>
        </head>
        <body>
        <div class="container">
        <div class="header">
          <div class="logo">${nombreNegocio}</div>
          <div class="title">RECIBO DE PARQUEO</div>
        </div>
        <div class="secuencial">N° ${secuencial}</div>
        <div class="reimpresion">*** REIMPRESIÓN ***</div>
        <div class="vehicle">
          <div class="vehicle-label">VEHÍCULO</div>
          <div class="plate">${d.placa}</div>
        </div>
        <div class="grace-period">
          <div class="grace-label">SALIDA MÁXIMA A:</div>
          <div class="grace-time">${fmtTimeShort(horaMaximaSalida)}</div>
          <div class="grace-subtext">Tiempo: ${minutosGraciaConfig} min</div>
        </div>
        <div class="section">
          <div class="section-title">TIEMPO DE PERMANENCIA</div>
          <div class="row"><span class="label">Entrada:</span><span class="value">${fmt(fechaEntrada)} ${fmtTimeShort(fechaEntrada)}</span></div>
          <div class="row"><span class="label">Pago:</span><span class="value">${fmt(fechaPago)} ${fmtTimeShort(fechaPago)}</span></div>
          <div class="row" style="border-top:1px dashed #000;padding-top:2px;font-weight:900;text-align:center"><span style="width:100%">Total: ${detalleTiempo}</span></div>
        </div>
        <div class="section">
          <div class="section-title">DESGLOSE DE TARIFA</div>
          <div class="breakdown">
            <div class="break-row"><span>Horas:</span><span>${d.horas_cobradas} hrs</span></div>
            ${d.tarifa_grupo_nombre ? `<div class="break-row"><span>Tarifa:</span><span>${d.tarifa_grupo_nombre}${d.tarifa_categoria_label ? ` (${d.tarifa_categoria_label})` : ''}</span></div>` : ''}
            ${horasDescontadas > 0 ? `<div class="break-row"><span>Facturas:</span><span>${facturas.length}</span></div><div class="break-row"><span>Dscto:</span><span>-${horasDescontadas}h</span></div>` : ''}
            ${d.ticket_perdido ? `<div class="break-row" style="border-top:1px dashed #000;padding-top:1px"><span>Ticket perdido:</span><span>$${valorTicketPerdido.toFixed(2)}</span></div>` : ''}
          </div>
        </div>
        <div class="total">
          <div class="total-amount">$${d.valor.toFixed(2)}</div>
          <div class="total-label">TOTAL PAGADO</div>
        </div>
        <div class="client">
          <div class="client-title">CLIENTE</div>
          <div class="row"><span class="label">Nombre:</span><span class="value">${clienteNombre}</span></div>
          <div class="row"><span class="label">C.I.:</span><span class="value">${clienteId}</span></div>
          <div class="row"><span class="label">Método:</span><span class="value">${metodo.toUpperCase()}</span></div>
        </div>
        <div class="footer">
          <div>${fmt(ahora)} | ${fmtTime(ahora)}</div>
          <div class="disclaimer">El establecimiento no se responsabiliza por robo o pérdida de vehículos</div>
          <div>www.tglabsecuador.com</div>
        </div>
        </div>
        <script>window.onload=()=>{setTimeout(()=>window.print(),300)}</script>
        </body>
        </html>`;

            const ventana = window.open('', '_blank');
            ventana.document.write(html);
            ventana.document.close();

          } catch (error) {
            enqueueSnackbar(`❌ Error: ${error.message}`, {
              variant: 'error', autoHideDuration: 6000,
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            });
          }
        };



  // El servidor ya devuelve esta página en el orden pedido (ver orderBy/
  // orderDir arriba) — no hace falta reordenar en el cliente.
  const displayVehicles = vehicles;

  const handleOpenObservationsDialog = (vehicle) => {
    setObservationsDialog({
      open: true,
      vehicle: vehicle,
    });
  };

  const handleImageClick = (img) => {
    setZoomImage({
      open: true,
      url: img.url,
      name: img.name,
      type: img.type,
      timestamp: img.timestamp
    });
  };
  
  const handleCloseObservationsDialog = () => {
    setObservationsDialog({
      open: false,
      vehicle: null,
    });
  };


  /**
   * Convertir imagen_entrada e imagen_salida a formato de array para el diálogo
   * @param {Object} vehicle - Datos del vehículo
   * @returns {Array} Array de imágenes formateadas
   */
  const getVehicleImages = (vehicle) => {
    const images = [];

    if (vehicle.imagen_entrada) {
      const src = buildImageSrc(vehicle.imagen_entrada, 'entrada');
      if (src) images.push({
        id: `entrada_${vehicle.id}`,
        url: src,
        name: "Imagen de Entrada",
        type: "entrada",
        timestamp: vehicle.fecha_entrada,
      });
    }

    if (vehicle.imagen_salida) {
      const src = buildImageSrc(vehicle.imagen_salida, 'salida');
      if (src) images.push({
        id: `salida_${vehicle.id}`,
        url: src,
        name: "Imagen de Salida",
        type: "salida",
        timestamp: vehicle.fecha_salida,
      });
    }
    return images;
  };

  /**
   * Verificar si el vehículo tiene imágenes
   * @param {Object} vehicle - Datos del vehículo
   * @returns {boolean} True si tiene al menos una imagen
   */
  const hasImages = (vehicle) => {
    return vehicle.imagen_entrada || vehicle.imagen_salida;
  };

  // Mantener exitDialog.vehicle fresco: cuando la lista se actualiza por SSE
  // o recarga manual, el vehículo en el diálogo recibe los últimos datos
  // (incluyendo fecha_pago actualizada tras el cobro).
  useEffect(() => {
    if (!exitDialog.open || !exitDialog.vehicle) return;
    const fresh = vehicles.find((v) => v.id === exitDialog.vehicle.id);
    if (fresh && fresh !== exitDialog.vehicle) {
      setExitDialog((prev) => ({ ...prev, vehicle: fresh }));
    }
  }, [vehicles]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenExitDialog = (vehicleId) => {
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    setExitDialog({
      open: true,
      vehicle: vehicle,
    });
  };

  const handleConfirmExit = async (vehicleId, observations) => {
    try {
      // Mostrar loading/info inicial
      enqueueSnackbar('Registrando salida del vehículo...', {
        variant: 'info',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
  
      // Llamar al servicio para registrar la salida
      const result = await vehicleService.registrarSalida(vehicleId, {
        observaciones_salida: observations || "Sin observaciones"
      });

      if (result.requierePago) {
        // No se pudo salir gratis (plan/tarjeta no cubre el momento, o ya
        // venció la gracia) — abrir el cobro directo para ese vehículo.
        enqueueSnackbar(`${result.error} — abriendo cobro`, {
          variant: 'warning',
          autoHideDuration: 4000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        });
        handleOpenPaymentDialog(result.vehiculo);
        return;
      }

      if (result.success) {
        // Mostrar éxito
        enqueueSnackbar('✅ Salida registrada exitosamente', {
          variant: 'success',
          autoHideDuration: 4000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
        });
  
        // Opcional: mostrar tiempo de permanencia si está disponible
        if (result.tiempo_permanencia) {
          setTimeout(() => {
            enqueueSnackbar(`⏱️ Tiempo de permanencia: ${result.tiempo_permanencia}`, {
              variant: 'info',
              autoHideDuration: 5000,
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
            });
          }, 1500);
        }
  
        // Recargar la página o actualizar los datos
        setTimeout(() => {
          window.location.reload(); // O mejor: llamar a una función para recargar datos
        }, 2000);
  
      } else {
        // Mostrar error del resultado
        enqueueSnackbar(`❌ ${result.error || 'Error al registrar salida'}`, {
          variant: 'error',
          autoHideDuration: 6000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
        });
      }
    } catch (error) {
      console.error('Error registrando salida:', error);
      
      // Mostrar error de excepción
      enqueueSnackbar(`❌ Error: ${error.message || 'Error inesperado'}`, {
        variant: 'error',
        autoHideDuration: 6000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
    } finally {
      // Cerrar el diálogo siempre
      setExitDialog({
        open: false,
        vehicle: null,
      });
    }
  };

  const handleCloseExitDialog = () => {
    setExitDialog({
      open: false,
      vehicle: null,
    });
  };

  const handleViewImages = (vehicle) => {
    const images = getVehicleImages(vehicle);
    setImageDialog({
      open: true,
      images,
      vehicleInfo: {
        placa: vehicle.numero_placa,
        estado: vehicle.estado,
        fecha_entrada: vehicle.fecha_entrada,
        fecha_salida: vehicle.fecha_salida,
      },
    });
  };

  function isValidPlateFormat(plate) {
    if (!plate || typeof plate !== 'string') {
      return false;
    }
    
    // Eliminar espacios y convertir a mayúsculas
    const cleanPlate = plate.trim().toUpperCase();
    
    // Patrones de placas válidas:
    // ABC1234 - 3 letras seguidas de 4 números
    // AB1234  - 2 letras seguidas de 4 números  
    // ABC123  - 3 letras seguidas de 3 números
    const patterns = [
      /^[A-Z]{3}[0-9]{4}$/, // ABC1234
      /^[A-Z]{2}[0-9]{4}$/,  // AB1234
      /^[A-Z]{3}[0-9]{3}$/   // ABC123
    ];
    if (patterns.some(pattern => pattern.test(cleanPlate))){
      return plate
    }
    return false ;
  }

  

  const formatImageTimestamp = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleString("es-EC", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const formatPaymentAmount = (vehicle) => {
    if (!vehicle?.pagado || vehicle?.valor_pagado === null || vehicle?.valor_pagado === undefined) {
      return "-";
    }

    const amount = Number(vehicle.valor_pagado);
    if (Number.isNaN(amount)) {
      return "-";
    }

    return `$${amount.toFixed(2)}`;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <StyledPaper elevation={0}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableHeaderCell sx={{ textAlign: "center" }}>
                  Imágenes
                </StyledTableHeaderCell>
                <StyledTableHeaderCell>Placa</StyledTableHeaderCell>
                <StyledTableHeaderCell>
                  Color
                </StyledTableHeaderCell>
                <StyledTableHeaderCell>
                  Tipo
                </StyledTableHeaderCell>
                <StyledTableHeaderCell sx={{ textAlign: "center" }}>
                  Calza
                </StyledTableHeaderCell>
                <StyledTableHeaderCell>
                  Sucursal
                </StyledTableHeaderCell>
                <StyledTableHeaderCell>
                  <StyledSortLabel
                    active={orderBy === "fecha_entrada"}
                    direction={orderBy === "fecha_entrada" ? orderDir : "desc"}
                    onClick={() => handleSort("fecha_entrada")}
                  >
                    Fecha y Hora de Entrada
                  </StyledSortLabel>
                </StyledTableHeaderCell>
                <StyledTableHeaderCell>
                  <StyledSortLabel
                    active={orderBy === "fecha_salida"}
                    direction={orderBy === "fecha_salida" ? orderDir : "desc"}
                    onClick={() => handleSort("fecha_salida")}
                  >
                    Fecha y Hora de Salida
                  </StyledSortLabel>
                </StyledTableHeaderCell>
                <StyledTableHeaderCell>Observaciones</StyledTableHeaderCell>
                 <StyledTableHeaderCell sx={{ textAlign: "center" }}>Pago</StyledTableHeaderCell>
                <StyledTableHeaderCell sx={{ textAlign: "center" }}>
                  Valor del Pago
                </StyledTableHeaderCell>
                <StyledTableHeaderCell sx={{ textAlign: "center" }}>
                  Acciones
                </StyledTableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayVehicles.map((vehicle) => (
                <StyledTableRow key={vehicle.id}>
                  {/* Imágenes */}
                  <StyledTableCell sx={{ textAlign: "center" }}>
                    {hasImages(vehicle) ? (
                      <Tooltip title="Ver imágenes del vehículo" arrow>
                        <StyledImageButton
                          size="small"
                          onClick={() => handleViewImages(vehicle)}
                        >
                          <PhotoCameraIcon fontSize="small" />
                          {/* Indicador de cantidad de imágenes */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: -4,
                              right: -4,
                              backgroundColor: "#1976d2",
                              color: "white",
                              borderRadius: "50%",
                              width: 16,
                              height: 16,
                              fontSize: "10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {getVehicleImages(vehicle).length}
                          </Box>
                        </StyledImageButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Sin imágenes disponibles" arrow>
                        <IconButton
                          size="small"
                          disabled
                          sx={{
                            color: "#9ca3af",
                            cursor: "not-allowed",
                          }}
                        >
                          <ImageNotSupportedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </StyledTableCell>

                  {/* Placa (o cédula, cuando el vehículo se identificó así al entrar) */}
                  <StyledTableCell>
                  <StyledText>
                    {(() => {
                      const placaValida = isValidPlateFormat(vehicle?.numero_placa) || isValidPlateFormat(vehicle?.numero_placa_auxiliar);
                      if (placaValida) return placaValida;
                      const valor = vehicle?.numero_placa || vehicle?.numero_placa_auxiliar;
                      const esCedula = !!valor && /^[0-9]{8,10}$/.test(valor.trim());
                      if (esCedula) {
                        return <>{valor} <span style={{ color: "#3b82f6", fontWeight: 600 }}>(cédula)</span></>;
                      }
                      return valor
                        ? valor
                        : <span style={{ color: "#FFA500" }}>⚠️ Sin placa</span>;
                    })()}
                  </StyledText>
                </StyledTableCell>


                  {/* Color del Vehículo */}
                  <StyledTableCell>
                    <StyledText>
                      {vehicle.color_vehiculo || "No especificado"}
                    </StyledText>
                  </StyledTableCell>

                  {/* Tipo de Vehículo */}
                  <StyledTableCell>
                    <StyledText>
                      {vehicle.tipo_vehiculo_label || vehicle.tipo_vehiculo || "No especificado"}
                    </StyledText>
                  </StyledTableCell>

                  {/* Calza / puesto físico (asignado desde el POS) */}
                  <StyledTableCell sx={{ textAlign: "center" }}>
                    <StyledText>
                      {vehicle.numero_calza || "—"}
                    </StyledText>
                  </StyledTableCell>

                  {/* Sucursal */}
                  <StyledTableCell>
                    <StyledText>
                      {vehicle.sucursal_nombre || "—"}
                    </StyledText>
                  </StyledTableCell>

                  {/* Fecha de entrada */}
                  <StyledTableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "center",
                      }}
                    >
                      <StyledText>
                        {formatDateTime
                          ? formatDateTime(vehicle.fecha_entrada)
                          : new Date(vehicle.fecha_entrada).toLocaleString(
                              "es-EC"
                            )}
                      </StyledText>
                      <StyledStatusChip
                        label={<ArrowDownward fontSize="small" />}
                        size="small"
                        status={"active"}
                      />
                    </Box>
                  </StyledTableCell>

                  {/* Fecha de salida */}
                  <StyledTableCell>
                    {vehicle.fecha_salida ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        <StyledText>
                          {formatDateTime
                            ? formatDateTime(vehicle.fecha_salida)
                            : new Date(vehicle.fecha_salida).toLocaleString(
                                "es-EC"
                              )}
                        </StyledText>
                        <StyledStatusChip
                          label={<ArrowUpward fontSize="small" />}
                          size="small"
                          status={"completed"}
                        />
                      </Box>
                    ) : (
                      <StyledPendingText
                        style={{ gap: "8px", textAlign: "center" }}
                      >
                        -
                      </StyledPendingText>
                    )}
                  </StyledTableCell>
                 {/* Observaciones */}
                  <StyledTableCell sx={{ textAlign: "center" }}>
                    {vehicle.observaciones_entrada ||
                    vehicle.observaciones_salida ? (
                      <Tooltip title="Ver observaciones" arrow>
                        <StyledImageButton
                          size="small"
                          onClick={() => handleOpenObservationsDialog(vehicle)}
                        >
                          <Visibility fontSize="small" />
                        </StyledImageButton>
                      </Tooltip>
                    ) : (
                      <StyledPendingText
                        style={{ gap: "8px", textAlign: "center" }}
                      >
                        -
                      </StyledPendingText>
                    )}
                  </StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "center" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 14px",
                        borderRadius: 15,
                        backgroundColor: vehicle?.pagado
                          ? "#2e7d32"
                          : "#c34644ff",
                        color: "#fff",
                        minWidth: 50,
                      }}
                    >
                      {vehicle?.pagado ? "Sí" : "No"}
                    </span>
                  </StyledTableCell>

                  <StyledTableCell sx={{ textAlign: "center" }}>
                    <StyledText>{formatPaymentAmount(vehicle)}</StyledText>
                  </StyledTableCell>

                  {/* Acciones */}
                  <StyledTableCell sx={{ textAlign: "center" }}>
                    {vehicle.estado === "dentro" ? (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        {/* Botón para registrar salida */}
                        <Tooltip title="Registrar salida del vehículo" arrow>
                          <StyledActionButton
                            size="small"
                            onClick={() => handleOpenExitDialog(vehicle.id)}
                            disabled={loading || !vehicle?.pagado}
                            sx={{
                              backgroundColor: "#ef4444",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#dc2626",
                              },
                            }}
                          >
                            <ExitToAppIcon fontSize="small" />
                          </StyledActionButton>
                        </Tooltip>

                        {user?.role !== "guard" && !vehicle?.tiene_plan && (
                          <Tooltip title="Registrar cobro" arrow>
                            <StyledActionButton
                              size="small"
                              onClick={() => handleOpenPaymentDialog(vehicle)}
                              disabled={loading || vehicle?.pagado}
                              sx={{
                                backgroundColor: "#f8b643",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "#f8b643",
                                },
                              }}
                            >
                              <AttachMoney fontSize="small" />
                            </StyledActionButton>
                          </Tooltip>
                        )}

                        {/* Botón para obtener QR */}
                        {user?.role !== "guard" && <Tooltip title="Ver QR" arrow>
                          <StyledActionButton
                            size="small"
                            onClick={() => onPrintQR(vehicle)}
                            disabled={loading}
                            sx={{
                              backgroundColor: "#3b82f6",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#2563eb",
                              },
                            }}
                          >
                            <QrCode fontSize="small" />
                          </StyledActionButton>
                        </Tooltip>}

                        {vehicle?.pagado && user?.role !== "guard" && (
                            <Tooltip title="Reimprimir recibo de cobro" arrow>
                              <StyledActionButton
                                size="small"
                                onClick={() => onReimprimirRecibo ? onReimprimirRecibo(vehicle) : handleReprintReceipt(vehicle)}
                                disabled={loading}
                                sx={{
                                  backgroundColor: "#10b981",
                                  color: "white",
                                  "&:hover": { backgroundColor: "#059669" },
                                }}
                              >
                                <PrintIcon fontSize="small" />
                              </StyledActionButton>
                            </Tooltip>
                          )}

                        {vehicle?.pagado && (
                          <Tooltip title="Ver cuánto pagó y de cuántos minutos" arrow>
                            <StyledActionButton
                              size="small"
                              onClick={() => handleVerDetallePago(vehicle)}
                              disabled={loading}
                              sx={{
                                backgroundColor: "#8b5cf6",
                                color: "white",
                                "&:hover": { backgroundColor: "#7c3aed" },
                              }}
                            >
                              <ReceiptIcon fontSize="small" />
                            </StyledActionButton>
                          </Tooltip>
                        )}

                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        {/* Icono de salida deshabilitado */}
                        <Tooltip title="Vehículo ya salió" arrow>
                          <IconButton
                            size="small"
                            disabled
                            sx={{
                              color: "#64748b",
                              cursor: "not-allowed",
                              backgroundColor: "#f1f5f9",
                            }}
                          >
                            <ExitToAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Botón QR disponible para vehículos que ya salieron */}
                        {user?.role !== "guard" && <Tooltip title="Ver QR"  arrow>
                          <StyledActionButton
                            size="small"
                            onClick={() => onPrintQR(vehicle)}
                            disabled={loading}
                            sx={{
                              backgroundColor: "#3b82f6",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#2563eb",
                              },
                            }}
                          >
                            <QrCode fontSize="small" />
                          </StyledActionButton>
                        </Tooltip>}

                        {vehicle?.pagado && (
                          <Tooltip title="Ver cuánto pagó y de cuántos minutos" arrow>
                            <StyledActionButton
                              size="small"
                              onClick={() => handleVerDetallePago(vehicle)}
                              disabled={loading}
                              sx={{
                                backgroundColor: "#8b5cf6",
                                color: "white",
                                "&:hover": { backgroundColor: "#7c3aed" },
                              }}
                            >
                              <ReceiptIcon fontSize="small" />
                            </StyledActionButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      </StyledPaper>

      {/* Dialog: cuánto pagó y de cuántos minutos — reutiliza los mismos
          datos que ya trae el recibo para reimprimir, solo que en pantalla. */}
      <Dialog
        open={detallePagoDialog.open}
        onClose={() => setDetallePagoDialog({ open: false, loading: false, data: null, error: null })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: "#282626", color: "white", display: "flex", alignItems: "center", gap: 1 }}>
          <ReceiptIcon fontSize="small" />
          Detalle de pago
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {detallePagoDialog.loading && (
            <Typography variant="body2" sx={{ color: "#888", textAlign: "center", py: 3 }}>
              Cargando…
            </Typography>
          )}
          {detallePagoDialog.error && (
            <Typography variant="body2" sx={{ color: "#c34644", textAlign: "center", py: 3 }}>
              {detallePagoDialog.error}
            </Typography>
          )}
          {detallePagoDialog.data && (() => {
            const d = detallePagoDialog.data;
            return (
              <Box>
                <Box sx={{ textAlign: "center", mb: 2 }}>
                  <Typography variant="h4" fontWeight={900} sx={{ color: "#282626" }}>
                    ${Number(d.valor || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#888", display: "block" }}>
                    Placa {d.placa}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap", mt: 0.75 }}>
                    {d.placa_autorizada && (
                      <Chip size="small" label="Placa autorizada"
                        sx={{ bgcolor: "#ede9fe", color: "#6d28d9", fontWeight: 700, fontSize: 11 }} />
                    )}
                    {d.plan_nombre && (
                      <Chip size="small" label={`Plan: ${d.plan_nombre}`}
                        sx={{ bgcolor: "#ede9fe", color: "#6d28d9", fontWeight: 700, fontSize: 11 }} />
                    )}
                    {d.tipo && d.tipo !== "normal" && (
                      <Chip size="small" label={TIPO_COBRO_LABEL[d.tipo] || d.tipo}
                        sx={{ bgcolor: TIPO_COBRO_COLOR[d.tipo]?.bg || "#f1f5f9", color: TIPO_COBRO_COLOR[d.tipo]?.fg || "#475569", fontWeight: 700, fontSize: 11 }} />
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid #eee" }}>
                  <Typography variant="body2" color="#666">Tiempo dentro</Typography>
                  <Typography variant="body2" fontWeight={600}>{formatearTiempo(d.horas_permanencia)}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid #eee" }}>
                  <Typography variant="body2" color="#666">Tiempo que se le cobró</Typography>
                  <Typography variant="body2" fontWeight={600}>{formatearTiempo(d.horas_cobradas)}</Typography>
                </Box>
                {Number(d.horas_descontadas) > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid #eee" }}>
                    <Typography variant="body2" color="#666">Descontado (facturas)</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatearTiempo(d.horas_descontadas)}</Typography>
                  </Box>
                )}
                {(d.tarifa_grupo_nombre || d.tarifa_categoria_label) && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid #eee" }}>
                    <Typography variant="body2" color="#666">Tarifa aplicada</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {d.tarifa_grupo_nombre}{d.tarifa_categoria_label ? ` (${d.tarifa_categoria_label})` : ""}
                    </Typography>
                  </Box>
                )}
                {d.ticket_perdido && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid #eee" }}>
                    <Typography variant="body2" color="#666">Cargo por ticket perdido</Typography>
                    <Typography variant="body2" fontWeight={600}>${Number(d.valor_ticket_perdido || 0).toFixed(2)}</Typography>
                  </Box>
                )}
                {d.categoria_cobro_nombre && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid #eee" }}>
                    <Typography variant="body2" color="#666">Categoría de cobro</Typography>
                    <Typography variant="body2" fontWeight={600}>{d.categoria_cobro_nombre}</Typography>
                  </Box>
                )}
                {d.horario_cobro_nombre && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid #eee" }}>
                    <Typography variant="body2" color="#666">Horario aplicado</Typography>
                    <Typography variant="body2" fontWeight={600}>{d.horario_cobro_nombre}</Typography>
                  </Box>
                )}
                {d.descuento_nombre && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid #eee" }}>
                    <Typography variant="body2" color="#666">Descuento aplicado</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {d.descuento_nombre}{d.descuento_valor != null ? ` (-$${Number(d.descuento_valor).toFixed(2)})` : ""}
                    </Typography>
                  </Box>
                )}
                {Number(d.valor) === 0 && d.observaciones_salida && (
                  <Box sx={{ py: 0.75, borderBottom: "1px solid #eee" }}>
                    <Typography variant="body2" color="#666">Motivo de la salida sin cobro</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>{d.observaciones_salida}</Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid #eee" }}>
                  <Typography variant="body2" color="#666">Método de pago</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ textTransform: "capitalize" }}>
                    {d.comprobante?.metodo || "-"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid #eee" }}>
                  <Typography variant="body2" color="#666">Fecha de pago</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {d.fecha_pago ? new Date(d.fecha_pago).toLocaleString("es-EC") : "-"}
                  </Typography>
                </Box>
                {d.operador_nombre && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}>
                    <Typography variant="body2" color="#666">Cobrado por</Typography>
                    <Typography variant="body2" fontWeight={600}>{d.operador_nombre}</Typography>
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDetallePagoDialog({ open: false, loading: false, data: null, error: null })}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para mostrar imágenes */}
      <Dialog
        open={imageDialog.open}
        onClose={() =>
          setImageDialog({ open: false, images: [], vehicleInfo: null })
        }
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            overflow: "hidden",
          },
        }}
      >
        {/* Header del diálogo */}
        <DialogTitle
          sx={{
            backgroundColor: "#282626",
            color: "white",
            p: 0,
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 3,
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  borderRadius: 2,
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="700">
                  Imágenes del Vehículo
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                  Placa: <strong>{imageDialog.vehicleInfo?.placa}</strong>
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() =>
                setImageDialog({ open: false, images: [], vehicleInfo: null })
              }
              sx={{
                color: "white",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
                borderRadius: 2,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Contenido del diálogo */}
        <DialogContent sx={{ p: 0, backgroundColor: "#f8fafc" }}>
          {imageDialog.images && imageDialog.images.length > 0 ? (
            <Box sx={{ p: 3 }}>
              {/* Contador de imágenes */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                  pb: 2,
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <Typography variant="h6" fontWeight="600" color="text.primary">
                  Imágenes Registradas
                </Typography>
                <Chip
                  label={`${imageDialog.images.length} ${
                    imageDialog.images.length === 1 ? "imagen" : "imágenes"
                  }`}
                  sx={{
                    backgroundColor: "#282626",
                    color: "white",
                    fontWeight: "600",
                  }}
                />
              </Box>

              {/* Grid de imágenes */}
              <ImageList cols={2} rowHeight={320} gap={20}>
                {imageDialog.images.map((img, index) => (
                  <ImageListItem
                    key={img.id}
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      boxShadow: "0 10px 25px -3px rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      position: "relative",
                      cursor: "pointer",
                    }}
                    
                    onClick={() => handleImageClick(img)}
                  >
                    {/* Imagen principal */}
                    <img
                      src={img.url}
                      alt={img.name}
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: 280,
                        objectFit: "cover",
                        transition: "transform 0.3s ease",
                      }}
                      onError={(e) => {
                        // Fallback en caso de error de imagen
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />

                    {/* Fallback para imágenes que no cargan */}
                    <Box
                      sx={{
                        width: "100%",
                        height: 280,
                        display: "none",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f3f4f6",
                        color: "#6b7280",
                      }}
                    >
                      <Box textAlign="center">
                        <ImageNotSupportedIcon sx={{ fontSize: 48, mb: 1 }} />
                        <Typography variant="body2">
                          Imagen no disponible
                        </Typography>
                      </Box>
                    </Box>

                    {/* Overlay con información */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background:
                          "linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.8) 100%)",
                        opacity: 1,
                        transition: "opacity 0.3s ease",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        p: 2,
                        color: "white",
                        "&:hover": {
                          opacity: 0,
                        },
                      }}
                    >
                      {/* Tipo de imagen */}
                      <Box sx={{ alignSelf: "flex-start" }}>
                        <Chip
                          label={img.type === "entrada" ? "Entrada" : "Salida"}
                          size="small"
                          sx={{
                            backgroundColor:
                              img.type === "entrada"
                                ? "rgba(34, 197, 94, 0.8)"
                                : "rgba(239, 68, 68, 0.8)",
                            color: "white",
                            backdropFilter: "blur(10px)",
                            fontWeight: "600",
                          }}
                        />
                      </Box>

                      {/* Información de la imagen */}
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight="700"
                          sx={{ mb: 0.5 }}
                        >
                          {img.name}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                          {formatImageTimestamp(img.timestamp)}
                        </Typography>
                      </Box>
                    </Box>
                  </ImageListItem>
                ))}
              </ImageList>
            </Box>
          ) : (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <ImageNotSupportedIcon
                sx={{ fontSize: 64, color: "grey.400", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary">
                No hay imágenes disponibles
              </Typography>
            </Box>
          )}
        </DialogContent>

        {/* Footer del diálogo */}
        <DialogActions
          sx={{
            p: 3,
            backgroundColor: "white",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <Button
            variant="outlined"
            onClick={() =>
              setImageDialog({ open: false, images: [], vehicleInfo: null })
            }
            sx={{
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
              borderRadius: 2,
              px: 3,
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      {/* Dialog para mostrar observaciones */}
<Dialog
  open={observationsDialog.open}
  onClose={handleCloseObservationsDialog}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 4,
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    },
  }}
>
  <DialogTitle
    sx={{
      backgroundColor: "#282626",
      color: "white",
      p: 0,
      position: "relative",
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 3,
      }}
    >
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            borderRadius: 2,
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Visibility sx={{ fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight="700">
            Observaciones del Vehículo
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            Placa: <strong>{observationsDialog.vehicle?.numero_placa || observationsDialog.vehicle?.numero_placa_auxiliar || "Sin placa"}</strong>
          </Typography>
        </Box>
      </Box>
      <IconButton
        onClick={handleCloseObservationsDialog}
        sx={{
          color: "white",
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
          borderRadius: 2,
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  </DialogTitle>

  <DialogContent sx={{ p: 3, backgroundColor: "#f8fafc" }}>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Observaciones de Entrada */}
      <Box>
        <Typography variant="h6" fontWeight="600" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1, mt: 3 }}>
          <ArrowDownward fontSize="small" sx={{ color: "#22c55e" }} />
          Observaciones de Entrada
        </Typography>
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            p: 2,
            minHeight: 60,
          }}
        >
          <Typography variant="body1" color="text.primary">
            {observationsDialog.vehicle?.observaciones_entrada || (
              <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                Sin observaciones de entrada
              </span>
            )}
          </Typography>
        </Box>
      </Box>

      {/* Observaciones de Salida */}
      <Box>
        <Typography variant="h6" fontWeight="600" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <ArrowUpward fontSize="small" sx={{ color: "#ef4444" }} />
          Observaciones de Salida
        </Typography>
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            p: 2,
            minHeight: 60,
          }}
        >
          <Typography variant="body1" color="text.primary">
            {observationsDialog.vehicle?.observaciones_salida || (
              <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                {observationsDialog.vehicle?.estado === "dentro" 
                  ? "Vehículo aún no ha salido" 
                  : "Sin observaciones de salida"}
              </span>
            )}
          </Typography>
        </Box>
      </Box>
    </Box>
  </DialogContent>

  <DialogActions
    sx={{
      p: 3,
      backgroundColor: "white",
      borderTop: "1px solid #e2e8f0",
    }}
  >
    <Button
      variant="outlined"
      onClick={handleCloseObservationsDialog}
      sx={{
        borderColor: "#d1d5db",
        color: "#374151",
        "&:hover": {
          borderColor: "#9ca3af",
          backgroundColor: "#f9fafb",
        },
        borderRadius: 2,
        px: 3,
      }}
    >
      Cerrar
    </Button>
  </DialogActions>
</Dialog>

<Dialog
  open={zoomImage.open}
  onClose={() => setZoomImage({ open: false, url: '', name: '', type: '', timestamp: '' })}
  maxWidth={false}
  fullWidth
  PaperProps={{
    sx: {
      backgroundColor: "rgba(0, 0, 0, 0.9)",
      boxShadow: "none",
      margin: 0,
      maxHeight: "100vh",
      maxWidth: "100vw",
    },
  }}
>
  <DialogContent sx={{ p: 0, position: "relative", textAlign: "center" }}>
    {/* Botón cerrar */}
    <IconButton
      onClick={() => setZoomImage({ open: false, url: '', name: '', type: '', timestamp: '' })}
      sx={{
        position: "absolute",
        top: 20,
        right: 20,
        color: "white",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
        zIndex: 1,
      }}
    >
      <CloseIcon />
    </IconButton>

    {/* Información de la imagen */}
    <Box
      sx={{
        position: "absolute",
        bottom: 20,
        left: 20,
        color: "white",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        backdropFilter: "blur(10px)",
        borderRadius: 2,
        p: 2,
        zIndex: 1,
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Chip
          label={zoomImage.type === "entrada" ? "Entrada" : "Salida"}
          size="small"
          sx={{
            backgroundColor: zoomImage.type === "entrada" 
              ? "rgba(34, 197, 94, 0.8)" 
              : "rgba(239, 68, 68, 0.8)",
            color: "white",
            fontWeight: "600",
          }}
        />
      </Box>
      <Typography variant="h6" fontWeight="700">
        {zoomImage.name}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.9 }}>
        {formatImageTimestamp(zoomImage.timestamp)}
      </Typography>
    </Box>

    {/* Imagen ampliada */}
    <img
      src={zoomImage.url}
      alt={zoomImage.name}
      style={{
        maxWidth: "100%",
        maxHeight: "100vh",
        objectFit: "contain",
        cursor: "zoom-out",
      }}
      onClick={() => setZoomImage({ open: false, url: '', name: '', type: '', timestamp: '' })}
    />
  </DialogContent>
</Dialog>
      <VehicleExitDialog
        open={exitDialog.open}
        onClose={handleCloseExitDialog}
        vehicle={exitDialog.vehicle}
        onConfirmExit={handleConfirmExit}
        loading={loading}
        loadVehicles={loadVehicles}
      />
       <PaymentDialog
        open={paymentDialog.open}
        onClose={handleClosePaymentDialog}
        vehicle={paymentDialog.vehicle}
        loadVehicles={loadVehicles}
      />

    </Box>
  );
};

export default VehicleTable;
